/*
 * <sticky-atc> — sticky add-to-cart bar for the PDP (cro template).
 *
 * Reuses the live product flow instead of re-implementing it:
 *   - The add button proxies a click to the section's real submit button
 *     (#ProductSubmitButton-{section}), so product-form.js handles the add,
 *     cart drawer/notification and error handling.
 *   - The variant pills proxy the real <variant-selects> radios, so
 *     VariantSelects (global.js) runs the native variant change.
 *   - It subscribes to PUB_SUB_EVENTS.variantChange to keep its price,
 *     thumbnail, active pill and button state in sync.
 *
 * Visibility: shown once the main ATC scrolls out of view, hidden again when
 * the footer enters view so the bar never covers footer content.
 */
if (!customElements.get('sticky-atc')) {
  customElements.define(
    'sticky-atc',
    class StickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.isPrimaryButtonOutOfView = false;
        this.isFooterInView = false;

        this.cacheElements();
        this.bindEvents();
        this.observeVisibility();
        this.syncAddButtonState();
      }

      disconnectedCallback() {
        this.variantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.cartErrorUnsubscriber?.();
        this.primaryButtonObserver?.disconnect();
        this.footerObserver?.disconnect();
      }

      cacheElements() {
        this.addButton = this.querySelector('.sticky-atc__submit');
        this.loadingSpinner = this.addButton?.querySelector('.loading__spinner');
        this.productAddButton = this.addButton ? document.getElementById(this.addButton.dataset.target) : null;
        this.priceContainers = this.querySelectorAll('[data-sticky-price]');
        this.thumbnailImage = this.querySelector('.sticky-atc__thumb img');
        this.variantButtons = Array.from(this.querySelectorAll('.sticky-atc__variant'));
        this.variantPicker = document.getElementById(`variant-selects-${this.sectionId}`);
      }

      bindEvents() {
        this.handleAddToCart = this.handleAddToCart.bind(this);
        this.handleVariantClick = this.handleVariantClick.bind(this);
        this.handleVariantChange = this.handleVariantChange.bind(this);
        this.resetLoadingState = this.resetLoadingState.bind(this);

        this.addButton?.addEventListener('click', this.handleAddToCart);
        this.variantButtons.forEach((button) => button.addEventListener('click', this.handleVariantClick));

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.handleVariantChange);
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.resetLoadingState);
        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, this.resetLoadingState);
      }

      observeVisibility() {
        if (!('IntersectionObserver' in window)) return;

        if (this.productAddButton) {
          this.primaryButtonObserver = new IntersectionObserver(
            ([entry]) => {
              this.isPrimaryButtonOutOfView = !entry.isIntersecting;
              this.updateVisibility();
            },
            { threshold: 0 }
          );
          this.primaryButtonObserver.observe(this.productAddButton);
        }

        const footer = document.querySelector('.footer, footer');
        if (footer) {
          this.footerObserver = new IntersectionObserver(
            ([entry]) => {
              this.isFooterInView = entry.isIntersecting;
              this.updateVisibility();
            },
            { threshold: 0 }
          );
          this.footerObserver.observe(footer);
        }
      }

      updateVisibility() {
        const shouldShow = this.isPrimaryButtonOutOfView && !this.isFooterInView;
        this.classList.toggle('sticky-atc--visible', shouldShow);
      }

      handleAddToCart(event) {
        event.preventDefault();
        if (
          !this.productAddButton ||
          this.addButton.getAttribute('aria-disabled') === 'true' ||
          this.addButton.getAttribute('aria-busy') === 'true'
        )
          return;
        // Show the same spinner the native button uses, then route through the real form.
        this.addButton.classList.add('sticky-atc__submit--loading');
        this.loadingSpinner?.classList.remove('hidden');
        this.addButton.setAttribute('aria-busy', 'true');
        this.productAddButton.click();
      }

      handleVariantClick(event) {
        if (!this.variantPicker) return;
        const optionValue = event.currentTarget.dataset.optionValue;
        if (optionValue == null) return;
        const matchingRadio = this.variantPicker.querySelector(`input[type="radio"][value="${CSS.escape(optionValue)}"]`);
        if (matchingRadio) {
          if (!matchingRadio.checked) {
            // Drive the native variant change — same flow as the main picker.
            matchingRadio.checked = true;
            matchingRadio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        } else {
          // Dropdown variant layout: drive the native <select> instead.
          const select = this.variantPicker.querySelector('select');
          if (select && select.value !== optionValue) {
            select.value = optionValue;
            select.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      }

      handleVariantChange(event) {
        if (event.data.sectionId !== this.sectionId) return;
        const variant = event.data.variant;
        this.syncPriceFromProduct();
        this.updateThumbnail(variant);
        this.updateActiveVariant(variant);
        this.syncAddButtonState();
      }

      // Mirror the live main price markup so formatting/badges always match.
      syncPriceFromProduct() {
        const livePrice = document.getElementById(`price-${this.sectionId}`);
        if (!livePrice) return;
        this.priceContainers.forEach((container) => {
          container.innerHTML = livePrice.innerHTML;
        });
      }

      updateThumbnail(variant) {
        if (!this.thumbnailImage) return;
        // Fall back to the product's default image when the variant has none.
        const src = variant?.featured_image?.src || this.thumbnailImage.dataset.defaultSrc;
        if (!src) return;
        const imageUrl = new URL(src, window.location.href);
        imageUrl.searchParams.set('width', '132');
        this.thumbnailImage.src = imageUrl.toString();
      }

      updateActiveVariant(variant) {
        if (!variant) return;
        this.variantButtons.forEach((button) => {
          const isActive = Number(button.dataset.variantId) === variant.id;
          button.classList.toggle('sticky-atc__variant--selected', isActive);
          button.setAttribute('aria-pressed', isActive ? 'true' : 'false');
        });
      }

      // Keep the proxy button's label and disabled state aligned with the real one.
      syncAddButtonState() {
        if (!this.productAddButton || !this.addButton) return;
        const isDisabled = this.productAddButton.hasAttribute('disabled');
        this.addButton.classList.toggle('sticky-atc__submit--disabled', isDisabled);
        this.addButton.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');

        const nativeLabel = this.productAddButton.querySelector('span')?.textContent?.trim();
        const stickyLabel = this.addButton.querySelector('.sticky-atc__submit-text');
        if (nativeLabel && stickyLabel) stickyLabel.textContent = nativeLabel;
      }

      resetLoadingState() {
        this.addButton?.classList.remove('sticky-atc__submit--loading');
        this.loadingSpinner?.classList.add('hidden');
        this.addButton?.removeAttribute('aria-busy');
      }
    }
  );
}
